import { useMutation } from '@apollo/client';
import { datadogRum } from '@datadog/browser-rum';
import { useAccountContext } from '@seek/hirer-graphql-react';
import { adapter } from '@seek/tealium-adapter';
import { useTranslations } from '@vocab/react';
import { Alert, Stack, Text } from 'braid-design-system';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import type {
  HirerAccountFragment,
  IntegrationRelationshipType,
  RequestIntegrationRelationshipsSuccess,
  RequestRelationshipsMutation,
  RequestRelationshipsMutationVariables,
} from 'src/App/generated/graphql-types';
import { REQUEST_RELATIONSHIPS } from 'src/gql/mutations/requestRelationships';
import { useIntegrationPartners } from 'src/utils/hooks/useIntegrationPartners';

import translations from '../.vocab';

import { PartnerSelection } from './PartnerSelection';
import { ReceivedRequest } from './ReceivedRequest/ReceivedRequest';
import { Request } from './Request/Request';
import type { RadioWithFollowUpState } from './Request/components/Form/components/RadioWithFollowUp/RadioWithFollowUp';

type HirerAccount = HirerAccountFragment & { selected?: boolean };
type Solution = IntegrationRelationshipType & { selected?: boolean };

export interface FormValues {
  hirerAccounts: HirerAccount[];
  solutions: Solution[];
  contactName: string;
  goLiveDate: string;
  reviewTerms: boolean;
  futurePosting?: RadioWithFollowUpState;
  futureApplications?: RadioWithFollowUpState;
}

const tealium = adapter();

export const ContentState = () => {
  const { accountContext } = useAccountContext();
  const { selectedPartner } = useIntegrationPartners();
  const { t } = useTranslations(translations);

  const [requestResult, setRequestResult] =
    useState<RequestIntegrationRelationshipsSuccess>();
  const [requestError, setRequestError] = useState<string>();

  const [requestRelationships] = useMutation<
    RequestRelationshipsMutation,
    RequestRelationshipsMutationVariables
  >(REQUEST_RELATIONSHIPS, {
    onError: (err) => {
      datadogRum.addError(err, { err });
    },
  });

  const defaultValues: FormValues = {
    hirerAccounts: [],
    solutions: selectedPartner
      ? selectedPartner.configurableRelationships.map((r) => ({
          selected: false,
          ...r,
        }))
      : [],
    contactName: accountContext?.currentUser?.name ?? '',
    goLiveDate: '',
    reviewTerms: false,
  };

  const methods = useForm<FormValues>({
    defaultValues,
    reValidateMode: 'onChange',
  });

  const { handleSubmit, setValue, getValues } = methods;

  useEffect(() => {
    if (selectedPartner) {
      setValue(
        'solutions',
        selectedPartner.configurableRelationships.map((r) => ({
          selected: false,
          ...r,
        })),
      );
    }
  }, [selectedPartner, setValue]);

  const setContactName = useCallback(() => {
    const firstName = accountContext?.currentUser?.firstName;
    const lastName = accountContext?.currentUser?.lastName;

    if (!firstName && !lastName) return undefined;
    if (!lastName) return firstName;
    return `${firstName} ${lastName}`;
  }, [accountContext]);

  useEffect(() => {
    const contactName = getValues('contactName') || setContactName();
    if (contactName) {
      setValue('contactName', contactName);
    }
  }, [accountContext, setValue, getValues, setContactName]);

  const onValid = async (data: FormValues) => {
    setRequestError(undefined);
    const hirerAccountIds = data.hirerAccounts
      .filter(({ selected }) => selected)
      .map(({ id }) => id);
    const relationshipTypeCodes = data.solutions
      .filter(({ selected }) => selected)
      .map(({ typeCode }) => typeCode);

    if (selectedPartner) {
      const { data: payload, errors } = await requestRelationships({
        variables: {
          input: {
            hirerAccountIds,
            relationshipTypeCodes,
            partnerId: selectedPartner.id,
            contactName: data.contactName,
            goLiveDate: data.goLiveDate,
            futureApplications: data.futureApplications
              ? {
                  answer: data.futureApplications.answer,
                  followUpAnswer: data.futureApplications.followUpAnswer,
                }
              : undefined,
            futurePosting: data.futurePosting
              ? {
                  answer: data.futurePosting.answer,
                  followUpAnswer: data.futurePosting.followUpAnswer,
                }
              : undefined,
          },
        },
      });

      if (
        payload &&
        payload.requestIntegrationRelationships.__typename ===
          'RequestIntegrationRelationshipsSuccess'
      ) {
        setRequestResult(payload.requestIntegrationRelationships);
        tealium.link('link-your-software-form-submit', {
          page: 'link-your-software',
          siteSection: 'partners',
        });
        return;
      }

      if (errors) {
        setRequestError(t('submissionError'));
        return;
      }
    }
  };

  return (
    <>
      <Stack space="large">
        <PartnerSelection />
        {selectedPartner && (
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onValid)}>
              {!requestResult && <Request />}
              {requestResult && selectedPartner && (
                <ReceivedRequest
                  partner={selectedPartner}
                  hirers={getValues('hirerAccounts').filter(
                    ({ selected }) => selected,
                  )}
                  relationshipTypeCodes={getValues('solutions').filter(
                    ({ selected }) => selected,
                  )}
                  requestResult={requestResult}
                />
              )}
              {requestError && (
                <Alert id="lys-request-failure-alert">
                  <Text>{requestError}</Text>
                </Alert>
              )}
            </form>
          </FormProvider>
        )}
      </Stack>
    </>
  );
};
